import { html, LitElement, unsafeCSS } from "lit-element"
import { classMap } from "lit-html/directives/class-map"
import { styleMap } from "lit-html/directives/style-map"
import style from "./splitter.css"
import commonStyle from "../common.css"
import { debounce } from "../utils"

const MESSAGE_OPEN = "開く"
const MESSAGE_CLOSE = "閉じる"

/**
 * スプリッタ部品
 */
export default class extends LitElement {
	static styles = [unsafeCSS(style), unsafeCSS(commonStyle)]
	static get properties () {
		return {
			isFoldable: { type: Boolean, attribute: "is-foldable" },
			isFixedWidth: { type: Boolean, attribute: "is-fixed-width" },
			width: { type: Number, attribute: "width" },
			minWidth: { type: Number, attribute: "min-width" },
			maxWidth: { type: Number, attribute: "max-width" }
		}
	}

	#isLeftPaneColsed = false
	#enableAnimation = false

	/**
	 * スプリッタのドラッグイベント。
	 * Gridの再描画トリガーとするため発火しており、ドラッグ毎に毎回再描画しないようdebounceを設けている。
	 */
	#dispatchDragEvent = debounce(_ => {
		this.dispatchEvent(new CustomEvent("splitter-debounced-dragged"))
	}, 100)

	constructor () {
		super()
		this.width = 300
		this.minWidth = 0
		this.isFoldable = false
		this.isFixedWidth = false
	}

	render () {
		const [
			iconDirection,
			tooltip,
			position
		] = this.#isLeftPaneColsed ? [
			"right", MESSAGE_OPEN, "right"
		] : [
			"left", MESSAGE_CLOSE, "top"
		]

		return html`
			<div class="splitter ${this.#classes}" style=${this.#style} @transitionend=${this.#paneTogleEnd}>
				<div class="left-pane">
					<slot></slot>
				</div>
				<div class="splitter-pane hide-on-sp ${this.#splitterPaneClasses}" @mousedown=${this.#mousedown}>
					${this.isFoldable ? html`<bui-icon data-tooltip="${tooltip}" data-position="${position}" icon="fas fa-angle-double-${iconDirection}" class="hl icon-button" @click=${this.toggleLeftPane}></bui-icon>` : ""}
				</div>
			</div>
    `
	}

	firstUpdated () {
		const icon = this.shadowRoot.querySelector("bui-icon")
		if (icon) window.bui.Tooltip.init(icon)
	}

	get #style () {
		const width = this.#isLeftPaneColsed ? 0 : this.width
		const styles = {
			width: `${width}px`
		}
		if (this.#enableAnimation) {
			styles.transition = "width 0.2s ease-in-out"
		}
		return styleMap(styles)
	}

	get #classes () {
		return classMap({
			"is-left-pane-closed": this.#isLeftPaneColsed
		})
	}

	get #splitterPaneClasses () {
		return classMap({
			"is-fixed": this.isFixedWidth || this.#isLeftPaneColsed
		})
	}

	#mousedown () {
		if (this.isFixedWidth) return

		/**
		 *
		 * @param {MouseEvent} e
		 */
		const mousemove = e => {
			if (e.buttons !== 1) {
				removeEvents()
			}

			this.#dispatchDragEvent(e)

			let newWidth = this.width + e.movementX * (1 / window.devicePixelRatio) // movementXは解像度に影響される

			if (newWidth < this.minWidth) {
				newWidth = this.minWidth
				removeEvents()
			} else if (this.maxWidth != null && newWidth > this.maxWidth) {
				newWidth = this.maxWidth
				removeEvents()
			}
			this.width = newWidth
		}
		const removeEvents = () => {
			window.removeEventListener("mousemove", mousemove)
			window.removeEventListener("mouseup", removeEvents)
		}
		window.addEventListener("mousemove", mousemove)
		window.addEventListener("mouseup", removeEvents, { once: true })
	}

	/**
	 * 左ペインの折り畳み状態をトグルする。
	 */
	async toggleLeftPane () {
		this.#enableAnimation = true
		await this.requestUpdate()
		this.#isLeftPaneColsed = !this.#isLeftPaneColsed
		this.dispatchEvent(new CustomEvent("splitter-toggled", {
			detail: this.#isLeftPaneColsed
		}))
		await this.requestUpdate()
	}

	#paneTogleEnd (e) {
		if (e.target !== e.currentTarget) return
		this.#enableAnimation = false
		this.dispatchEvent(new CustomEvent("splitter-toggle-animation-end", {
			detail: this.#isLeftPaneColsed
		}))
		this.requestUpdate()
	}
}
